/**
 * Debug API client for journey trace harness.
 * Provides type-safe access to the backend debug endpoints.
 */

import { API_CONFIG } from '../config';
import { useAuthStore } from '../stores/authStore';

// =============================================================================
// Types matching backend Pydantic models (api/models/debug_models.py)
// =============================================================================

export interface PositionState {
  lat: number;
  lng: number;
  source: string;
  age_s: number | null;
}

export interface V5SegmentState {
  state: string;
  from_stop: string | null;
  to_stop: string | null;
  progress: number | null;
  authority: string | null;
  tier: number | null;
  gps_age_s: number | null;
  delay_s: number | null;
}

export interface PipelineTimestamps {
  last_v4_ingest: string | null;
  last_v1da_ingest: string | null;
  last_dedup_rejection: string | null;
  last_db_write: string | null;
  last_v5_computation: string | null;
  last_ws_broadcast: string | null;
}

export interface PipelineHealth {
  v4_ingests: number;
  v1da_ingests: number;
  dedup_rejections: number;
}

export interface JourneyState {
  journey_gid: string;
  journey_uuid: string;
  positions: Record<string, PositionState> | null;
  v5_segment: V5SegmentState | null;
  timestamps: PipelineTimestamps | null;
  pipeline_health: PipelineHealth | null;
  trace_active: boolean;
}

export interface TraceActivation {
  journey_gid: string;
  expires_at: string;
  ttl: number;
}

export interface TraceCheckpoint {
  ts: string;
  stage: string;
  journey_gid: string;
  source: string;
  data: Record<string, unknown>;
}

// =============================================================================
// Helpers
// =============================================================================

const BASE = `${API_CONFIG.REST_BASE}/debug`;

function getAuthHeaders(): Record<string, string> {
  const { accessToken } = useAuthStore.getState();
  if (!accessToken) return {};
  return { Authorization: `Bearer ${accessToken}` };
}

async function debugFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Debug API error ${response.status}: ${errorText}`);
  }

  return response.json() as Promise<T>;
}

// =============================================================================
// API functions
// =============================================================================

/**
 * Fetch the current pipeline state for a journey.
 * GET /api/v1/debug/journey/{gid}
 */
export async function fetchJourneyState(gid: string): Promise<JourneyState> {
  return debugFetch<JourneyState>(`${BASE}/journey/${encodeURIComponent(gid)}`);
}

/**
 * Activate a trace for a journey.
 * POST /api/v1/debug/trace/{gid}
 */
export async function activateTrace(gid: string, ttl = 1800): Promise<TraceActivation> {
  return debugFetch<TraceActivation>(`${BASE}/trace/${encodeURIComponent(gid)}`, {
    method: 'POST',
    body: JSON.stringify({ ttl }),
  });
}

/**
 * Deactivate a trace for a journey.
 * DELETE /api/v1/debug/trace/{gid}
 */
export async function deactivateTrace(gid: string): Promise<void> {
  const { accessToken } = useAuthStore.getState();
  await fetch(`${BASE}/trace/${encodeURIComponent(gid)}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
      ...(accessToken ? {} : {}),
    },
  });
}

/**
 * Create an EventSource for the trace SSE stream.
 * Passes the auth token as a query param since EventSource doesn't support headers.
 * GET /api/v1/debug/trace/{gid}/events?token=...
 */
export function createTraceEventSource(gid: string): EventSource {
  const { accessToken } = useAuthStore.getState();
  const url = `${BASE}/trace/${encodeURIComponent(gid)}/events${accessToken ? `?token=${accessToken}` : ''}`;
  return new EventSource(url);
}
